import { z } from "zod";
import { BUSINESS_UNITS, capitalizeName } from "../utils";
import { normalizeTimeToHHmmss } from "../utils/time-hhmmss";

export const required = (input: string) => `${input} est un champ requis`;
export const invalid = (input: string) => `${input} n'est pas valide`;

/**
 * Rebuilds Zod 3's `required_error` / `invalid_type_error` on top of Zod 4's single
 * `error` hook.
 *
 * The v4 hook is called for *every* issue a schema produces, not just type issues,
 * so it is gated by issue code: a missing value gets `whenMissing`, a rejected one
 * gets `whenInvalidType`, and a value failing a format check (`z.email()`,
 * `z.url()`, `z.guid()`, …) gets `whenInvalidFormat`. Every other issue
 * (`too_small`, `too_big`, …) returns `undefined` and therefore keeps the message
 * declared on the check itself, or Zod's default.
 *
 * Not every schema reports a missing value as `invalid_type`: enums have no
 * separate type check and report everything as `invalid_value`, and unions report
 * `invalid_union`. Both are gated in, otherwise `whenMissing` would silently never
 * reach the ~30 enum-backed form fields that rely on it.
 *
 * `whenInvalidFormat` exists because v4 turned the string formats into standalone
 * schemas, so `z.string({ ... }).email({ message })` collapses into a single
 * `z.email()` whose params must carry both messages.
 */
const MISSING_VALUE_ISSUE_CODES = new Set(["invalid_type", "invalid_value", "invalid_union"]);

export const buildTypeIssueMessage =
  ({
    whenMissing,
    whenInvalidType,
    whenInvalidFormat,
  }: {
    whenMissing?: string;
    whenInvalidType?: string;
    whenInvalidFormat?: string;
  }) =>
  (issue: z.core.$ZodRawIssue): string | undefined => {
    if (issue.code === "invalid_format") return whenInvalidFormat;
    if (!MISSING_VALUE_ISSUE_CODES.has(issue.code)) return undefined;
    return issue.input === undefined ? whenMissing : whenInvalidType;
  };

export const businessUnitNameEnumSchema: z.ZodType<(typeof BUSINESS_UNITS)[number]> = z.enum(BUSINESS_UNITS);
export type BusinessUnitNameEnum = z.infer<typeof businessUnitNameEnumSchema>;

export const zodDateSchema = z.coerce.date().transform((date) => date.toISOString());

/** TIME format HH:mm:ss (hours 00-23, minutes and seconds 00-59). Accepts HH:mm on input and normalizes. */
export const zodTimeHHmmssSchema = z.string().transform((value, ctx) => {
  const normalizedTime = normalizeTimeToHHmmss(value);
  if (!normalizedTime) {
    ctx.addIssue({
      code: "custom",
      message: "Format attendu : HH:mm:ss (ex. 22:00:00)",
    });
    return z.NEVER;
  }
  return normalizedTime;
});
export const zodNullableTimeHHmmssSchema = z.union([z.literal(null), zodTimeHHmmssSchema]);

export const zodNullableDateSchema = z.preprocess((val) => {
  if (val === null) return null;
  if (val instanceof Date) return val.toISOString();
  return val;
}, z.string().nullable());

export const cursorPaginationSchema = z.object({
  cursor_created_at: zodDateSchema.optional(),
  cursor_updated_at: zodDateSchema.optional(),
  cursor_end_date: zodDateSchema.optional(),
  cursor_name: z.string().optional(),
  cursor_id: z.string().optional(),
});

export const sortSchema = z.object({
  target: z.string().optional(),
  order: z.enum(["asc", "desc"]).default("asc").optional(),
});

export const offsetSchema = z.object({
  offset: z.coerce.number().default(0),
});

// These are utils function to preprocess the data before it's validated all wrapped in withPreprocessing()
// Used for parsing query params into arrays for example.

export const preprocessEmptyStringToUndefined = <T extends z.ZodType>(schema: T) =>
  z.preprocess((value) => {
    if (typeof value === "string" && value === "") {
      return undefined;
    }
    return value;
  }, schema);

/**
 * Refine option: Zod skips an object refine once a field is invalid; this runs it anyway on an object,
 * so a form shows every error at once.
 */
export const evenWithInvalidFields = {
  when: ({ value }: z.core.ParsePayload) => typeof value === "object" && value !== null,
};

export const preprocessEmptyStringToNull = <T extends z.ZodType>(schema: T) =>
  z.preprocess((value) => (value === "" ? null : value), schema);

const preprocessStringToArray = (value: unknown) => {
  if (typeof value === "string") {
    // Try to parse as JSON first (for complex objects like locations)
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Not valid JSON, fall back to comma-separated string
    }
    return value.split(",").filter(Boolean);
  }
  return value;
};

const preprocessStringToBoolean = (value: unknown) => {
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return value;
};

const preprocessStringToNumber = (value: unknown) => {
  if (typeof value === "string" && !isNaN(Number(value))) {
    return Number(value);
  }
  return value;
};

const preprocessDateToString = (value: unknown) => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  return value;
};

const preprocessNullStringToNull = (value: unknown) => {
  if (typeof value === "string" && value === "null") {
    return null;
  }
  return value;
};

const snakeToCamel = (str: string) => str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());

export const preprocessSnakeToCamel = <T extends z.ZodType>(schema: T) =>
  z.preprocess((data) => {
    if (!data || typeof data !== "object") return data;

    return Object.entries(data).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [snakeToCamel(key)]: value,
      }),
      {}
    );
  }, schema);

export const withPreprocessing = <T extends z.ZodType>(schema: T): T => {
  if (schema instanceof z.ZodObject) {
    const newShape = Object.fromEntries(
      Object.entries(schema.shape as Record<string, z.ZodType>).map(([key, value]) => {
        const innerSchema = value instanceof z.ZodOptional ? value.unwrap() : value;

        if (innerSchema instanceof z.ZodArray) {
          const processed = z.preprocess(preprocessStringToArray, innerSchema);
          return [key, value instanceof z.ZodOptional ? processed.optional() : processed];
        }

        if (innerSchema instanceof z.ZodBoolean) {
          const processed = z.preprocess(preprocessStringToBoolean, innerSchema);
          return [key, value instanceof z.ZodOptional ? processed.optional() : processed];
        }
        if (innerSchema instanceof z.ZodDate) {
          const processed = z.preprocess(preprocessDateToString, innerSchema);
          return [key, value instanceof z.ZodOptional ? processed.optional() : processed];
        }

        if (innerSchema instanceof z.ZodNumber) {
          const processed = z.preprocess(preprocessStringToNumber, innerSchema);
          return [key, value instanceof z.ZodOptional ? processed.optional() : processed];
        }

        if (innerSchema instanceof z.ZodString) {
          const processed = z.preprocess(preprocessNullStringToNull, innerSchema);
          return [key, value instanceof z.ZodOptional ? processed.optional() : processed];
        }

        return [key, value];
      })
    );

    return z.object(newShape) as unknown as T;
  }
  return schema;
};

export const withDateFormatting = <T extends z.ZodType>(schema: T): T => {
  if (schema instanceof z.ZodObject) {
    const newShape = Object.fromEntries(
      Object.entries(schema.shape as Record<string, z.ZodType>).map(([key, value]) => {
        if (value instanceof z.ZodString) {
          return [key, z.coerce.date()];
        }
        return [key, value];
      })
    );

    return z.object(newShape) as unknown as T;
  }
  return schema;
};

export const buildSafeStringSchema = (name: string) =>
  z
    .string({ error: buildTypeIssueMessage({ whenMissing: required(name) }) })
    .min(1)
    .transform((val) => val.trim())
    .refine((val) => val.length, { message: required(name) });

export const buildSafeNameSchema = (name: string) => buildSafeStringSchema(name).transform(capitalizeName);

export const phoneNumberSchema = z
  .string({ error: buildTypeIssueMessage({ whenMissing: required("Le numéro de téléphone") }) })
  .min(1)
  .transform((val) => val.trim())
  .pipe(
    z.string().refine(
      (val) => {
        // Matches international format with or without '+' and allows 8-15 digits
        return /^(\+)?[0-9]{8,15}$/.test(val);
      },
      {
        message: "Le numéro de téléphone doit contenir entre 8 et 15 chiffres et peut commencer par '+'",
      }
    )
  );

export const withRequiredError = <T extends z.ZodType>(schema: T, field: string) =>
  z.union([schema, z.never()], { error: buildTypeIssueMessage({ whenMissing: required(field) }) });

export const emptyBodySchema = z.object({});
export type EmptyBodySchema = z.infer<typeof emptyBodySchema>;
